import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ServicesPage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title
    document.title = 'Business Turnaround Services - Revivacorp Boca Raton';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Comprehensive business turnaround and restructuring services in Boca Raton. Cash flow coaching, cost reduction, process automation, capital solutions, and interim CXO services with fixed fees and timelines.');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/services`);
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Business Turnaround Services - Revivacorp Boca Raton' },
      { property: 'og:description', content: 'Comprehensive business turnaround and restructuring services in Boca Raton. Cash flow coaching, cost reduction, process automation, capital solutions, and interim CXO services with fixed fees and timelines.' },
      { property: 'og:url', content: `${siteUrl}/services` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: `${siteUrl}/og-image.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    // Set Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Business Turnaround Services - Revivacorp Boca Raton' },
      { name: 'twitter:description', content: 'Comprehensive business turnaround and restructuring services in Boca Raton. Cash flow coaching, cost reduction, process automation, capital solutions, and interim CXO services with fixed fees and timelines.' },
      { name: 'twitter:image', content: `${siteUrl}/og-image.jpg` }
    ];
    
    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name="${tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', tag.name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Business Turnaround & Restructuring Services',
      description: 'Comprehensive business turnaround and restructuring consulting services in Boca Raton. We help struggling corporations overcome financial challenges and achieve sustainable growth.',
      provider: {
        '@type': 'Organization',
        name: 'Revivacorp',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2255 Glades Rd',
          addressLocality: 'Boca Raton',
          addressRegion: 'FL',
          postalCode: '33431',
          addressCountry: 'US'
        }
      },
      areaServed: {
        '@type': 'Place',
        name: 'Boca Raton, FL'
      },
      serviceType: 'Business Consulting'
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Services',
          item: `${siteUrl}/services`
        }
      ]
    };

    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.text = JSON.stringify(serviceSchema);
    document.head.appendChild(serviceScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(serviceScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 min-h-[480px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/db8651306f75b8ceea576f017b020e49.jpg')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-800/60"></div>
        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">Our Services</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Comprehensive turnaround and restructuring solutions designed to stabilize distressed businesses, restore profitability, and save jobs — on a fixed fee and fixed timeline.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Cash Flow Coach */}
            <div className="bg-slate-50 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-funds-box-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">Cash Flow Coach</h3>
              <p className="text-slate-600 mb-6">
                Our proprietary cash flow forecast model gives distressed businesses the visibility they need to survive. Battle-tested, lender-approved, and supported by a dedicated Cash Flow Coach.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Rolling cash flow model</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Lender-ready reporting format</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Weekly coaching sessions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Variance analysis & corrective action</span>
                </li>
              </ul>
              <div className="pt-6 border-t border-slate-200">
                <Link to="/contact">
                  <button className="w-full px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                    Request Consultation
                  </button>
                </Link>
              </div>
            </div>

            {/* Business Turnaround Services */}
            <div className="bg-slate-50 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">Business Turnaround Services</h3>
              <p className="text-slate-600 mb-6">
                Cost-effective turnarounds built on a highly structured approach with a fixed fee and fixed timeline. We stabilize operations, reduce costs, and restore profitability.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Operational assessment & diagnosis</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Restructuring roadmap development</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Cost reduction & efficiency programs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Fixed fee & fixed timeline delivery</span>
                </li>
              </ul>
              <div className="pt-6 border-t border-slate-200">
                <Link to="/contact">
                  <button className="w-full px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                    Request Consultation
                  </button>
                </Link>
              </div>
            </div>

            {/* Cost Reduction */}
            <div className="bg-slate-50 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-scissors-cut-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">Cost Reduction</h3>
              <p className="text-slate-600 mb-6">
                We streamline your business by eliminating unnecessary expenditures, rightsizing your workforce, and building leaner, more resilient supply chains that protect your bottom line.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Non-essential spending elimination</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Workforce rightsizing strategies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Supply chain optimization</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Ongoing cost monitoring & reporting</span>
                </li>
              </ul>
              <div className="pt-6 border-t border-slate-200">
                <Link to="/contact">
                  <button className="w-full px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                    Request Consultation
                  </button>
                </Link>
              </div>
            </div>

            {/* Process Automation */}
            <div className="bg-slate-50 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">Process Automation</h3>
              <p className="text-slate-600 mb-6">
                We modernize your operations through smart technology upgrades and AI-driven solutions that eliminate bottlenecks, reduce manual effort, and unlock new levels of efficiency.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Technology stack assessment & upgrades</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">AI-driven workflow automation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Bottleneck identification & removal</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Efficiency benchmarking & optimization</span>
                </li>
              </ul>
              <div className="pt-6 border-t border-slate-200">
                <Link to="/contact">
                  <button className="w-full px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                    Request Consultation
                  </button>
                </Link>
              </div>
            </div>

            {/* Capital Solutions */}
            <div className="bg-slate-50 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">Capital Solutions</h3>
              <p className="text-slate-600 mb-6">
                We represent you in communicating with lenders and investors to determine the best financial decisions for your unique situation and secure the capital your business needs.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Lender negotiation & forbearance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Debt restructuring strategies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Alternative capital sourcing</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">SBA & government program guidance</span>
                </li>
              </ul>
              <div className="pt-6 border-t border-slate-200">
                <Link to="/contact">
                  <button className="w-full px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                    Request Consultation
                  </button>
                </Link>
              </div>
            </div>

            {/* CXO Services */}
            <div className="bg-slate-50 rounded-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">CXO Services</h3>
              <p className="text-slate-600 mb-6">
                Interim CFO, CEO, and COO services designed for the pace of insolvency and turnaround matters. We step in as experienced executives to stabilize and lead your business.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Interim CFO / CEO / COO placement</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Financial reporting & controls</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Stakeholder communication management</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-lg mr-2 mt-0.5"></i>
                  <span className="text-sm text-slate-700">Transition planning & handoff</span>
                </li>
              </ul>
              <div className="pt-6 border-t border-slate-200">
                <Link to="/contact">
                  <button className="w-full px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                    Request Consultation
                  </button>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Turnaround Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-slate-900 mb-4">Our Turnaround Process</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A proven five-step approach to stabilize, recover, and grow your business
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">01</span>
              </div>
              <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">Confidential Assessment</h3>
              <p className="text-sm text-slate-600">We start with a free, confidential assessment of your business situation — no obligation, no judgment.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">02</span>
              </div>
              <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">Diagnosis & Analysis</h3>
              <p className="text-sm text-slate-600">Our team conducts a thorough analysis of your cash flow, operations, debt structure, and stakeholder relationships.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">03</span>
              </div>
              <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">Turnaround Plan</h3>
              <p className="text-sm text-slate-600">We develop a structured, actionable turnaround plan with clear milestones, a fixed fee, and a fixed timeline.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">04</span>
              </div>
              <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">Implementation</h3>
              <p className="text-sm text-slate-600">We work alongside your team to execute the plan — negotiating with creditors, stabilizing cash flow, and restructuring operations.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">05</span>
              </div>
              <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">Stabilization & Exit</h3>
              <p className="text-sm text-slate-600">We ensure the business is stable and self-sustaining before we exit, with a clear handoff and ongoing support as needed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif font-bold text-slate-900 mb-6">Ready to Start Your Turnaround?</h2>
          <p className="text-lg text-slate-600 mb-8">
            Schedule a confidential, no-obligation assessment today. We'll help you understand your options and chart a path forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-4 bg-teal-500 text-white text-base font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                Get a Free Assessment
              </button>
            </Link>
            <Link to="/about">
              <button className="px-8 py-4 bg-slate-100 text-slate-900 text-base font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer">
                Learn About Our Team
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
